import {
  KeyboardEvent,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { twMerge as cn } from 'tailwind-merge';

/**
 * Accessible modal dialog primitive for the onboarding flow.
 *
 * Behaviors (addresses PR review on WCAG 2.1 SC 2.1.2 / 2.4.3 / 1.3.1):
 *   - Portal to document.body so z-index / overflow-hidden ancestors can't clip it.
 *   - Focus moved to initialFocusRef (or the first focusable descendant) on open.
 *   - Tab / Shift+Tab are trapped inside the panel.
 *   - Escape closes the dialog.
 *   - Focus is restored to the previously active element on close.
 *   - Body scroll is locked while open.
 *   - role="dialog", aria-modal="true", aria-labelledby, aria-describedby wired up.
 *
 * Intentional non-behaviors:
 *   - Backdrop click does NOT close (mirrors the existing ConfirmationDialog
 *     pattern elsewhere in the app; prevents losing typed-in form data on a
 *     stray click). Callers needing close-on-backdrop can handle it themselves.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** id of the element labelling this dialog (for aria-labelledby). */
  labelledBy: string;
  /** id of the element describing this dialog (for aria-describedby). Optional. */
  describedBy?: string;
  /** If provided, focus moves here on open. Otherwise the first focusable descendant is focused. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
  /** Classes applied to the inner panel. */
  panelClassName?: string;
  /** Classes applied to the backdrop wrapper. */
  backdropClassName?: string;
}

function getVisibleFocusables(panel: HTMLElement): HTMLElement[] {
  return Array.from(
    panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      // offsetParent === null also catches display:none; using width/height
      // bounds keeps visibility:hidden out without requiring layout reads.
      (el.offsetWidth > 0 || el.offsetHeight > 0),
  );
}

export function Dialog({
  open,
  onClose,
  labelledBy,
  describedBy,
  initialFocusRef,
  children,
  panelClassName,
  backdropClassName,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    // Remember whatever was focused before the dialog opened so we can
    // restore focus there on close (keeps keyboard users oriented).
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    // Move focus inside the panel on the next frame (after the portal mounts
    // and the browser has laid the panel out).
    const raf = requestAnimationFrame(() => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
        return;
      }
      const panel = panelRef.current;
      if (!panel) return;
      const first = getVisibleFocusables(panel)[0];
      if (first) first.focus();
      else panel.focus();
    });

    // Lock body scroll so content behind the dialog doesn't scroll on wheel.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
      const toRestore = previouslyFocusedRef.current;
      // Only restore focus if the element is still in the document and focusable.
      if (toRestore && document.contains(toRestore)) {
        toRestore.focus();
      }
    };
  }, [open, initialFocusRef]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusables = getVisibleFocusables(panel);
      if (focusables.length === 0) {
        // Nothing tabbable — keep focus pinned on the panel itself.
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  if (!open) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-black/50',
        backdropClassName,
      )}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={cn(
          'mx-4 w-full max-w-md rounded-xl border border-gray-300 bg-white p-6 shadow-xl ring-1 ring-gray-900/5 focus:outline-none',
          panelClassName,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
